"use client";
import LogoutButton from "@components/auth/LogOutButton";
import {
    logOutStyle,
    pageNavigationScrollerStyles,
} from "@components/cms/page-navigation/PageNavigationStyles";
import type { ILinksCollection } from "@contentful-api/types/contentful";
import { useAuth } from "@lib/auth/useAuth";
import { getPrivacyAttributes } from "@lib/monitoring/privacyAttributes";
import { H } from "@ecco/ui/a11y-heading";
import { Divider } from "@ecco/ui/divider";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import { space } from "@ecco/ui/tokens";
import { useBoundingRect } from "@ecco/ui/use-bounding-rect";
import { isEmpty } from "lodash-es";
import { useEffect, useRef } from "react";
import { PageNavLink } from "./components/link";
import { useLink } from "./hooks/useLink";

type PageNavigationProps = {
    title?: string;
    pageNavigationLinks: ILinksCollection;
    isPrivateDataInTitle?: boolean;
};

export const PageNavigation = ({
    pageNavigationLinks,
    title,
    isPrivateDataInTitle,
}: PageNavigationProps) => {
    const { isLoggedIn } = useAuth();
    const { ref, node } = useBoundingRect();

    const activeElementRef = useRef(null);

    const links = pageNavigationLinks?.fields?.links;
    const heading = title || pageNavigationLinks?.fields?.title;

    const { activeLink, onChangeActiveLink, getLinkTestId } = useLink({ links });

    useEffect(() => {
        // Scroll to active element
        // On mobile view at least 3 elements are visible
        if (node && activeElementRef?.current) {
            node.scrollTo({
                left: activeLink > 2 ? activeElementRef.current.clientWidth * (activeLink + 1) : 0,
                behavior: "smooth",
            });
        }
    }, [activeLink, node, activeElementRef?.current]);

    return (
        <Flex w="full" flexDirection="column" bg="gray.50" pl={[3, 3, 6]} pt={[6, 8]}>
            <Divider borderColor="gray.100" display={["block", "block", "none"]} />
            {heading && (
                <Flex>
                    <H
                        {...(isPrivateDataInTitle ? getPrivacyAttributes() : {})}
                        fontSize={["mobileH1", "tabletH1", "desktopH1"]}
                    >
                        {heading}
                    </H>
                </Flex>
            )}
            {!heading && isLoggedIn && <LogoutButton />}
            {!isEmpty(links) && (
                <Box overflowX="auto" w="full" pt={[10, 12]} data-testid="PageNavigation">
                    <Flex
                        gap={space["0.2"]}
                        pr={[2, null]}
                        alignItems="center"
                        sx={{
                            ...pageNavigationScrollerStyles,
                            scrollbarWidth: "initial",
                        }}
                        ref={ref}
                    >
                        {links?.map((link, index) => (
                            <PageNavLink
                                key={link?.sys?.id}
                                link={link}
                                isActive={index === activeLink}
                                onClick={onChangeActiveLink(index)}
                                testId={getLinkTestId(link)}
                            />
                        ))}
                        {isLoggedIn && (
                            <Box data-testid={"logout-desktop"}>
                                <LogoutButton sx={logOutStyle} />
                            </Box>
                        )}
                    </Flex>
                </Box>
            )}
        </Flex>
    );
};
